package dev.lingvo.site.pages.app

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.CreateWorkspaceRequest
import dev.lingvo.shared.Plan
import dev.lingvo.shared.PlanStatus
import dev.lingvo.shared.Role
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LAvatar
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LConfirmTone
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LPlusIcon
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.enterStaggered
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.nav.label
import dev.lingvo.site.state.WorkspaceSelection
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** Two free workspaces per person, and the page says so before anybody types a name. */
private const val FREE_WORKSPACE_LIMIT = 2

/**
 * Every workspace you are in.
 *
 * One card each, showing only what that role permits: a translator sees the name and their role, not
 * the plan or the member count, because neither is their business and both invite a question they
 * cannot act on.
 *
 * The free-workspace cap is **stated before typing** rather than delivered as a refusal afterwards.
 * A limit somebody discovers by hitting it is a limit that feels arbitrary.
 */
@Page("/app/workspaces")
@Layout(".components.layouts.AppLayout")
@Composable
fun WorkspacesPage() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    var refreshKey by remember { mutableStateOf(0) }
    var name by remember { mutableStateOf("") }
    // One flag per action rather than one for the page. Shared, creating a workspace put a spinner
    // on the Leave button of every card on screen, each of them claiming a request that was not in
    // flight; and leaving one disabled the create form.
    var creating by remember { mutableStateOf(false) }
    var leavingSlug by remember { mutableStateOf<String?>(null) }
    var confirmingLeave by remember { mutableStateOf<WorkspaceDto?>(null) }
    var createMessage by remember { mutableStateOf<String?>(null) }
    var leaveMessage by remember { mutableStateOf<String?>(null) }

    // `loadReloadable`, not `loadAsync(refreshKey)`. Creating or leaving a workspace bumps the same
    // counter the retry does, and as a *key* that counter discards the list before the request is
    // even sent — so the reader's own successful action blanked the page to skeleton bars and
    // cascaded every card back in, which reads as a reload rather than as one row appearing. The
    // reload token re-runs the request against the same subject and keeps the last good value up.
    val reloadable = loadReloadable(Session.requestIdentity, reloadToken = refreshKey) { ApiClient.listWorkspaces() }
    val state = reloadable.state
    // Read off the settled list so the header can carry the cap and the form. Loading, it is zero,
    // which leaves the field enabled for the split second before the answer arrives — and the
    // server refuses a third free workspace anyway, so nothing here is the only thing stopping it.
    val loaded = (state as? Async.Success)?.value.orEmpty()
    val owned = loaded.count { it.role == Role.OWNER && it.plan == Plan.FREE }
    val atLimit = owned >= FREE_WORKSPACE_LIMIT

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        // **The one thing to do on this page is make another workspace**, and it used to be a card
        // at the bottom of an arbitrarily long list of cards — below the fold on any account with
        // four memberships. The cap moves with it, into `meta`, so it is still stated before
        // anybody types rather than delivered as a refusal afterwards.
        PageHeader(
            t("app.workspaces.workspaces", "Workspaces"),
            t("app.workspaces.each_one_has_its_own_members", "Each one has its own members, projects, plan and credits."),
            meta = {
                // Counted through `tPlural` like the member and project counts below: a
                // concatenated "s" is English's rule applied to every language, in a product that
                // sells real plurals.
                LMuted(
                    if (atLimit) {
                        tPlural(
                            "app.workspaces.count.owned_free_workspace",
                            owned,
                            "You own {count} free workspace, which is the limit.",
                            "You own {count} free workspaces, which is the limit.",
                        ) + " " + t("app.workspaces.upgrade_one_or_ask_a_colleague", "Upgrade one, or ask a colleague to own the next — credits and " +
                            "project limits are per workspace, so this is how the free plan " +
                            "stays free.")
                    } else {
                        tPlural(
                            "app.workspaces.count.free_workspace_left",
                            FREE_WORKSPACE_LIMIT - owned,
                            "You can own {count} more free workspace.",
                            "You can own {count} more free workspaces.",
                        ) + " " + t("app.workspaces.credits_and_project_limits_are_per", "Credits and project limits are per workspace.")
                    },
                )
            },
        ) {
            Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.Bottom) {
                LTextField(
                    name,
                    { name = it },
                    label = t("app.workspaces.name", "Name"),
                    placeholder = t("app.workspaces.mobile_apps", "Mobile apps"),
                    enabled = !atLimit && !creating,
                )
                LButton(
                    t("app.workspaces.create", "Create"),
                    {
                        scope.launch {
                            creating = true
                            // The same fix `onLeave` below already carries. Left as
                            // `runCatching`, navigating away mid-create surfaced the
                            // CancellationException as "That didn't work" over a
                            // workspace that was in fact being created.
                            try {
                                ApiClient.createWorkspace(CreateWorkspaceRequest(name.trim()))
                                // The session holds the membership list every workspace control
                                // resolves against. Without this the new workspace was absent from
                                // it, so pressing "Switch to it" on the card that had just appeared
                                // fell through to the strongest membership and opened a different
                                // workspace with nothing said.
                                Session.loadMe()
                                name = ""
                                createMessage = null
                                refreshKey++
                            } catch (cancelled: CancellationException) {
                                throw cancelled
                            } catch (failure: Throwable) {
                                createMessage = failure.message
                                    ?: tr("app.workspaces.couldnt_create_this_workspace_retry", "Couldn't create this workspace. Retry?")
                            } finally {
                                creating = false
                            }
                        }
                    },
                    enabled = name.isNotBlank() && !atLimit && !creating,
                    loading = creating,
                    leadingIcon = { LPlusIcon() },
                )
            }
        }
        createMessage?.let {
            LAlert(
                t("app.workspaces.that_didnt_work", "That didn't work"),
                it,
                variant = LAlertVariant.WARNING,
                live = true,
                dense = true,
                onDismiss = { createMessage = null },
            )
        }

        LAsyncContent(
            state,
            rows = 3,
            loadingLabel = t("app.workspaces.loading_workspaces", "Loading workspaces"),
            onRetry = { refreshKey++ },
        ) { workspaces ->
            Column(Modifier.fillMaxWidth().gap(Space.XL)) {
                workspaces.forEachIndexed { index, workspace ->
                    WorkspaceCard(
                        index = index,
                        workspace = workspace,
                        // **"Open" used to route to /app without selecting anything**, so it
                        // opened whichever workspace was already current — on the one page whose
                        // entire job is switching between them. The only control that does switch
                        // is the header picker, which is hidden below the MD breakpoint, leaving a
                        // two-workspace admin on a phone acting on the wrong one with no way to
                        // notice and no way to change it.
                        onOpen = {
                            WorkspaceSelection.select(workspace.slug)
                            ctx.router.tryRoutingTo("/app")
                        },
                        current = workspace.slug == WorkspaceSelection.current()?.slug,
                        // Only the card whose request is actually in flight reports one.
                        leaving = leavingSlug == workspace.slug,
                        // Leaving is irreversible, so the button asks first. It used to fire the
                        // request on one click, from a small GHOST control sitting beside the
                        // "Switch to it" a reader on this page is usually reaching for.
                        onLeave = {
                            leaveMessage = null
                            confirmingLeave = workspace
                        },
                    )
                }
            }
        }

        // The refusal the server sends the last owner is read here, beside the button that was
        // pressed. It used to land in `message` and render at the foot of the page inside the "New
        // workspace" card, under a heading about creating one.
        LConfirmDialog(
            open = confirmingLeave != null,
            title = tf(
                "app.workspaces.leave_workspace",
                "Leave {workspace}?",
                "workspace" to confirmingLeave?.name.orEmpty(),
            ),
            confirmLabel = t("app.workspaces.leave", "Leave"),
            onConfirm = {
                val target = confirmingLeave ?: return@LConfirmDialog
                scope.launch {
                    leavingSlug = target.slug
                    try {
                        ApiClient.leaveWorkspace(target.slug)
                        // The membership list every workspace surface selects from lives in the
                        // session, and nothing here refreshed it: the workspace stayed in
                        // `Session.organizations`, so the header, the rail and every scoped page
                        // went on acting on one this person had just left.
                        Session.loadMe()
                        leaveMessage = null
                        confirmingLeave = null
                        refreshKey++
                    } catch (cancelled: CancellationException) {
                        throw cancelled
                    } catch (failure: Throwable) {
                        leaveMessage = failure.message
                            ?: tr("app.workspaces.couldnt_leave_this_workspace_retry", "Couldn't leave this workspace. Retry?")
                    } finally {
                        leavingSlug = null
                    }
                }
            },
            onCancel = {
                confirmingLeave = null
                leaveMessage = null
            },
            tone = LConfirmTone.DANGER,
            consequenceTitle = t("app.workspaces.this_takes_effect_immediately", "This takes effect immediately"),
            consequence = t("app.workspaces.you_lose_access_to_every_project", "You lose access to every project in it, and only an owner or admin of " +
                "that workspace can invite you back."),
            busy = leavingSlug != null,
            error = leaveMessage,
        )
    }
}

@Composable
private fun WorkspaceCard(
    workspace: WorkspaceDto,
    current: Boolean,
    onOpen: () -> Unit,
    leaving: Boolean,
    onLeave: () -> Unit,
    /** Position in the list, so several arrive as a sequence rather than a block. */
    index: Int = 0,
) {
    val palette = ColorMode.current.toSitePalette()
    // Only what this role may act on. A translator seeing "Free · 3 members" learns nothing they can
    // use and gains a question for somebody else.
    val manages = workspace.role == Role.OWNER || workspace.role == Role.ADMIN
    LCard(Modifier.fillMaxWidth().enterStaggered(index)) {
        Row(
            Modifier.fillMaxWidth().gap(Space.MD),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            // A monogram, so five cards in a list are five distinguishable things rather than five
            // identical rows of text. It carries the workspace's own name as its accessible label,
            // which is why the heading beside it is not repeated to a screen reader.
            LAvatar(workspace.name)
            Column(Modifier.flexGrow(1).gap(Space.XS)) {
                Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                    Span(Modifier.fontWeight(600).toAttrs()) { Text(workspace.name) }
                    // The catalogue's word, not `Role.name.lowercase()` — "owner" was English on
                    // every card in every language, and `VIEWER` read "viewer" rather than the
                    // word this product uses for somebody who can only read.
                    LBadge(workspace.role.label())
                    if (manages && workspace.planStatus == PlanStatus.PAST_DUE) {
                        // Amber, and on the card rather than only inside billing: a payment problem
                        // that is invisible until somebody opens the invoice page is a payment
                        // problem that becomes a suspension.
                        LBadge(t("app.workspaces.payment_failed", "payment failed"), variant = LBadgeVariant.WARNING)
                    }
                }
                if (manages) {
                    LMuted(
                        tf(
                            "app.workspaces.plan_name",
                            "{plan} · ",
                            // The plan's own name, from the enum that defines the product's
                            // three tiers. A plan is a proper noun — "Studio" is what the pricing
                            // page, the invoice and the upgrade dialog all call it — so it is not
                            // a translated word, and the four keys that used to translate it here
                            // were four chances for a workspace to be on a plan whose name nobody
                            // could find in the pricing table.
                            "plan" to workspace.plan.displayName,
                        ) +
                            tPlural(
                                "app.workspaces.count.member",
                                workspace.memberCount,
                                "{count} member",
                                "{count} members",
                            ) + " · " + tPlural(
                                "app.workspaces.count.project",
                                workspace.projectCount,
                                "{count} project",
                                "{count} projects",
                            ),
                    )
                }
            }
            if (current) {
                LBadge(t("app.workspaces.current", "Current"), variant = LBadgeVariant.SUCCESS)
            } else {
                // Named, like the Leave button twelve lines below it. Unnamed, a list of five
                // workspaces offered five buttons called "Switch to it" and nothing said to what.
                LButton(
                    t("app.workspaces.switch_to_it", "Switch to it"),
                    onOpen,
                    size = LButtonSize.SM,
                    variant = LButtonVariant.SECONDARY,
                    // Contains the visible words verbatim, then names the workspace. "Switch to
                    // Acme" reads better and is unreachable by speech: the reader can only say what
                    // is on the button, and "Switch to it" is not inside it.
                    accessibleLabel = tf(
                        "app.workspaces.switch_to_it_name",
                        "Switch to it: {name}",
                        "name" to workspace.name,
                    ),
                )
            }
            LButton(
                t("app.workspaces.leave", "Leave"),
                onLeave,
                size = LButtonSize.SM,
                variant = LButtonVariant.GHOST,
                // The server refuses the last owner and says why, and the dialog this opens is
                // where that reason is read. The button stays enabled so the refusal can be
                // reached at all — but it reports the request already in flight, so a second one
                // cannot be sent.
                enabled = !leaving,
                loading = leaving,
                accessibleLabel = tf("app.workspaces.leave_name", "Leave {name}", "name" to workspace.name),
            )
        }
    }
}
